/**
 * OpenAI Service for AI-powered features
 * Proxies requests through the backend API for security
 */

const API_BASE_URL = process.env.REACT_APP_BASE_URL || '';

/**
 * Check if OpenAI is configured on the backend
 * @returns {Promise<boolean>}
 */
export const checkOpenAIStatus = async () => {
  try {
    const response = await fetch(`${API_BASE_URL}/openai/status`);
    const data = await response.json();
    return data.configured === true;
  } catch (error) {
    console.error('[OpenAI Service] Status check failed:', error);
    return false;
  }
};

/**
 * Generate an AI summary of chat messages
 * @param {Array} messages - Array of chat message objects
 * @returns {Promise<string>} - AI-generated summary
 */
export async function generateChatSummary(messages) {
  if (!messages || messages.length === 0) {
    return 'No messages to summarize.';
  }

  try {
    const response = await fetch(`${API_BASE_URL}/openai/chat-summary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ messages }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data.summary;
  } catch (error) {
    console.error('[OpenAI Service] Chat summary error:', error);
    throw error;
  }
}

/**
 * Generate an AI summary of a meeting transcript
 * @param {Array} transcriptItems - Array of transcript objects with text and timestamp
 * @param {Array} manualNotes - Optional array of manual notes
 * @returns {Promise<Object>} - AI-generated summary with sections
 */
export async function generateTranscriptSummary(transcriptItems, manualNotes = []) {
  if (!transcriptItems || transcriptItems.length === 0) {
    return {
      summary: 'No transcript to summarize.',
      keyPoints: [],
      actionItems: [],
      decisions: [],
      topics: [],
    };
  }

  try {
    const response = await fetch(`${API_BASE_URL}/openai/transcript-summary`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ transcriptItems, manualNotes }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API error: ${response.status}`);
    }

    const data = await response.json();
    return {
      summary: data.summary || '',
      keyPoints: data.keyPoints || [],
      actionItems: data.actionItems || [],
      decisions: data.decisions || [],
      topics: data.topics || [],
    };
  } catch (error) {
    console.error('[OpenAI Service] Transcript summary error:', error);
    throw error;
  }
}

/**
 * Generate AI-powered meeting notes from transcript
 * @param {string} transcript - Full transcript text
 * @returns {Promise<string>} - Formatted meeting notes
 */
export async function generateMeetingNotes(transcript) {
  if (!transcript || transcript.trim().length === 0) {
    return 'No transcript provided for note generation.';
  }

  try {
    const response = await fetch(`${API_BASE_URL}/openai/meeting-notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ transcript }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data.notes;
  } catch (error) {
    console.error('[OpenAI Service] Meeting notes error:', error);
    throw error;
  }
}

/**
 * AI Assistant chat - conversational AI for meeting questions
 * @param {string} question - The user's question
 * @param {Array} conversationHistory - Previous messages in the conversation
 * @param {Object} meetingContext - Optional context about the current meeting
 * @returns {Promise<string>} - AI response
 */
export async function assistantChat(question, conversationHistory = [], meetingContext = null) {
  if (!question || question.trim().length === 0) {
    throw new Error('Question is required');
  }

  try {
    const response = await fetch(`${API_BASE_URL}/openai/assistant-chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ question, conversationHistory, meetingContext }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `API error: ${response.status}`);
    }

    const data = await response.json();
    return data.response;
  } catch (error) {
    console.error('[OpenAI Service] Assistant chat error:', error);
    throw error;
  }
}

/**
 * Generate webinar title suggestions
 * @param {Object} params - { description, topic }
 * @returns {Promise<string[]>} - Array of title suggestions
 */
export async function generateWebinarTitle({ description, topic } = {}) {
  const response = await fetch(`${API_BASE_URL}/openai/webinar-generate-title`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ description, topic }),
  });
  if (!response.ok) throw new Error('Failed to generate titles');
  const data = await response.json();
  return data.titles || [];
}

/**
 * Generate webinar description from title
 * @param {string} title
 * @returns {Promise<string>}
 */
export async function generateWebinarDescription(title) {
  const response = await fetch(`${API_BASE_URL}/openai/webinar-generate-description`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title }),
  });
  if (!response.ok) throw new Error('Failed to generate description');
  const data = await response.json();
  return data.description || '';
}

/**
 * Suggest optimal webinar times
 * @param {Object} params - { title, description, timezone }
 * @returns {Promise<Array>}
 */
export async function suggestWebinarTimes({ title, description, timezone } = {}) {
  const response = await fetch(`${API_BASE_URL}/openai/webinar-suggest-times`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, description, timezone }),
  });
  if (!response.ok) throw new Error('Failed to suggest times');
  const data = await response.json();
  return data.suggestions || [];
}

/**
 * Generate talking points for a webinar
 * @param {Object} params - { title, description }
 * @returns {Promise<Array>}
 */
export async function generateWebinarTalkingPoints({ title, description } = {}) {
  const response = await fetch(`${API_BASE_URL}/openai/webinar-talking-points`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ title, description }),
  });
  if (!response.ok) throw new Error('Failed to generate talking points');
  const data = await response.json();
  return data.talkingPoints || [];
}

export default {
  checkOpenAIStatus,
  generateChatSummary,
  generateTranscriptSummary,
  generateMeetingNotes,
  assistantChat,
  generateWebinarTitle,
  generateWebinarDescription,
  suggestWebinarTimes,
  generateWebinarTalkingPoints,
};
